(() => {
  if (window.OnlineBufferEngine && window.OnlineBufferEngine.__onlineBufferInstalled) return;

  const Engine = window.OnlineBufferEngine || {};
  Engine.__onlineBufferInstalled = true;
  Engine.version = "0.3.6";

  const state = {
    cart: null,
    cartFetchedAt: 0,
    cartPromise: null,
    cartQuantities: new Map(),
    pending: new Map(),
    refreshTimer: null,
    warningEl: null,
    warningTimer: null,
    productForms: new WeakMap(),
    productFormIntervals: new WeakMap(),
    cartDrawerTimer: null,
    enforcingCartDrawer: false,
    enforceCartDrawerQueued: false,
    cartDrawerObserver: null
  };

  const MST_APP_BLOCK_SELECTOR = '.shopify-app-block[id*="b2stock_back_in_stock_notify_app_block_v2"]';
  const MST_BUFFER_TITLE_TEXT = "More On The Way. Coming Soon!";
  const CART_DRAWER_ITEM_SELECTOR = "[data-online-buffer-cart-item]";
  const CART_PAGE_ITEM_SELECTOR = ".cart-item[data-online-buffer-applies][data-line]";
  const CART_QTY_INPUT_SELECTOR = "input[data-cart-quantity]";
  const CART_PLUS_SELECTOR = "[data-plus-quantity-cart]";
  const CART_MINUS_SELECTOR = "[data-minus-quantity-cart]";

  const now = () => Date.now();

  const toInt = (v) => {
    const n = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(n) ? n : 0;
  };

  const isTruthy = (v) => {
    const s = String(v ?? "").trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes" || s === "y";
  };

  const getPendingRecord = (variantId) => {
    const rec = state.pending.get(variantId);
    if (!rec) return null;
    if (rec.expiresAt <= now()) {
      state.pending.delete(variantId);
      return null;
    }
    return rec;
  };

  const getPendingQty = (variantId) => {
    const rec = getPendingRecord(variantId);
    return rec ? rec.qty : 0;
  };

  const addPending = (variantId, qty) => {
    const rec = getPendingRecord(variantId);
    const nextQty = (rec ? rec.qty : 0) + qty;
    state.pending.set(variantId, { qty: nextQty, expiresAt: now() + 4000 });
  };

  const reconcilePendingWithCart = (oldMap, newMap) => {
    for (const [variantId, rec] of state.pending.entries()) {
      if (rec.expiresAt <= now()) {
        state.pending.delete(variantId);
        continue;
      }
      const oldQty = toInt(oldMap.get(variantId) || 0);
      const newQty = toInt(newMap.get(variantId) || 0);
      const delta = newQty - oldQty;
      if (delta > 0) {
        const next = rec.qty - delta;
        if (next <= 0) state.pending.delete(variantId);
        else state.pending.set(variantId, { qty: next, expiresAt: rec.expiresAt });
      }
    }
  };

  const applyCartObject = (c, oldMapOverride) => {
    const oldMap = oldMapOverride || state.cartQuantities;

    state.cart = c;
    state.cartFetchedAt = now();
    state.cartPromise = null;

    const newMap = new Map();
    if (c && Array.isArray(c.items)) {
      for (const item of c.items) {
        newMap.set(String(item.variant_id), toInt(item.quantity));
      }
    }
    state.cartQuantities = newMap;
    reconcilePendingWithCart(oldMap, newMap);
  };

  const fetchCart = async (force = false) => {
    const age = now() - state.cartFetchedAt;
    if (!force && state.cart && age < 500) return state.cart;
    if (state.cartPromise) return state.cartPromise;

    const oldMap = state.cartQuantities;

    state.cartPromise = fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        applyCartObject(c, oldMap);
        return c;
      })
      .catch(() => null)
      .finally(() => {
        state.cartPromise = null;
      });

    return state.cartPromise;
  };

  const scheduleRefresh = () => {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = setTimeout(() => {
      fetchCart(true);
    }, 900);
  };

  const ensureWarningEl = () => {
    if (state.warningEl && document.body.contains(state.warningEl)) return state.warningEl;

    const oldToast = document.getElementById("online-buffer-toast");
    if (oldToast) oldToast.remove();

    const oldPopup = document.getElementById("online-buffer-warning-popup");
    if (oldPopup) oldPopup.remove();

    const existing = document.getElementById("online-buffer-warning-ob");
    if (existing) {
      state.warningEl = existing;
      return existing;
    }

    const el = document.createElement("div");
    el.id = "online-buffer-warning-ob";
    el.className = "halo-warning-popup-ob";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.innerHTML = `<button type="button" class="halo-popup-close-ob clearfix" data-online-buffer-close aria-label="Close"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M 38.982422 6.9707031 A 2.0002 2.0002 0 0 0 37.585938 7.5859375 L 24 21.171875 L 10.414062 7.5859375 A 2.0002 2.0002 0 0 0 8.9785156 6.9804688 A 2.0002 2.0002 0 0 0 7.5859375 10.414062 L 21.171875 24 L 7.5859375 37.585938 A 2.0002 2.0002 0 1 0 10.414062 40.414062 L 24 26.828125 L 37.585938 40.414062 A 2.0002 2.0002 0 1 0 40.414062 37.585938 L 26.828125 24 L 40.414062 10.414062 A 2.0002 2.0002 0 0 0 38.982422 6.9707031 z"></path></svg></button><span class="halo-warning-icon-ob"><svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 554.2 554.199" class="icon icon-warning"><g><path d="M538.5,386.199L356.5,70.8c-16.4-28.4-46.7-45.9-79.501-45.9c-32.8,0-63.1,17.5-79.5,45.9L12.3,391.6 c-16.4,28.4-16.4,63.4,0,91.8C28.7,511.8,59,529.3,91.8,529.3H462.2c0.101,0,0.2,0,0.2,0c50.7,0,91.8-41.101,91.8-91.8 C554.2,418.5,548.4,400.8,538.5,386.199z M316.3,416.899c0,21.7-16.7,38.3-39.2,38.3s-39.2-16.6-39.2-38.3V416 c0-21.601,16.7-38.301,39.2-38.301S316.3,394.3,316.3,416V416.899z M317.2,158.7L297.8,328.1c-1.3,12.2-9.4,19.8-20.7,19.8 s-19.4-7.7-20.7-19.8L237,158.6c-1.3-13.1,5.801-23,18-23H299.1C311.3,135.7,318.5,145.6,317.2,158.7z"></path></g></svg></span><div class="halo-warning-content-ob" data-online-buffer-content></div>`;
    document.body.appendChild(el);

    el.addEventListener("click", (e) => {
      if (e.target.closest("[data-online-buffer-close]")) {
        e.preventDefault();
        hideWarning();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") hideWarning();
    });

    state.warningEl = el;
    return el;
  };

  const hideWarning = () => {
    clearTimeout(state.warningTimer);
    document.body.classList.remove("has-warning-ob");
  };

  const showWarning = (message) => {
    const el = ensureWarningEl();
    const content = el.querySelector("[data-online-buffer-content]");
    if (content) content.textContent = message;

    clearTimeout(state.warningTimer);
    document.body.classList.add("has-warning-ob");
    state.warningTimer = setTimeout(() => {
      hideWarning();
    }, 2600);
  };

  const findFormFromButton = (btn) => {
    const direct = btn.closest("form");
    if (direct) return direct;
    const formId = btn.dataset ? btn.dataset.formId : null;
    if (!formId) return null;
    try {
      return document.querySelector(formId);
    } catch (e) {
      return null;
    }
  };

  const findBufferHost = (btn, form) => {
    if (btn && btn.dataset && btn.dataset.onlineBufferApplies != null) return btn;
    if (form && form.dataset && form.dataset.onlineBufferApplies != null) return form;
    const a = btn && btn.closest ? btn.closest("[data-online-buffer-applies]") : null;
    if (a) return a;
    const b = form && form.closest ? form.closest("[data-online-buffer-applies]") : null;
    if (b) return b;
    return null;
  };

  const isBufferedAddForm = (btn, form) => {
    if (!form) return false;
    const action = String(form.getAttribute("action") || "");
    if (action.indexOf("/cart/add") === -1) return false;

    const host = findBufferHost(btn, form);
    if (!host || !host.dataset) return false;

    if (!isTruthy(host.dataset.onlineBufferApplies)) return false;

    const buffer = toInt(host.dataset.onlineBuffer);
    if (buffer <= 0) return false;

    return true;
  };

  const getVariantId = (form) => {
    const input = form.querySelector('input[name="id"]');
    if (!input) return null;
    const v = String(input.value || "").trim();
    return v ? v : null;
  };

  const getAddQty = (form) => {
    const input = form.querySelector('input[name="quantity"]');
    const qty = toInt(input ? input.value : 1);
    return qty > 0 ? qty : 1;
  };

  const getMax = (btn, form) => {
    const btnMaxRaw = btn && btn.dataset ? btn.dataset.onlineBufferEffective : null;
    if (btnMaxRaw != null && btnMaxRaw !== "") {
      const m = toInt(btnMaxRaw);
      return m >= 0 ? m : 0;
    }

    const formMaxRaw = form && form.dataset ? form.dataset.onlineBufferEffective : null;
    if (formMaxRaw != null && formMaxRaw !== "") {
      const m = toInt(formMaxRaw);
      return m >= 0 ? m : 0;
    }

    const formMaxRaw2 = form && form.dataset ? form.dataset.onlineBufferEffectiveTotal : null;
    if (formMaxRaw2 != null && formMaxRaw2 !== "") {
      const m = toInt(formMaxRaw2);
      return m >= 0 ? m : 0;
    }

    const host = findBufferHost(btn, form);
    const hostMaxRaw = host && host.dataset ? host.dataset.onlineBufferEffective : null;
    if (hostMaxRaw != null && hostMaxRaw !== "") {
      const m = toInt(hostMaxRaw);
      return m >= 0 ? m : 0;
    }

    return null;
  };

  const handleAddAttempt = (btn, form, event) => {
    if (!isBufferedAddForm(btn, form)) return;

    const variantId = getVariantId(form);
    if (!variantId) return;

    const max = getMax(btn, form);
    if (max == null) return;

    if (max <= 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showWarning("This item is sold out online.");
      return;
    }

    const addQty = getAddQty(form);
    const cartQty = toInt(state.cartQuantities.get(variantId) || 0);
    const pendingQty = getPendingQty(variantId);
    const effectiveCurrent = cartQty + pendingQty;

    if (effectiveCurrent + addQty > max) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showWarning(`You can only add ${max} of this item online.`);
      return;
    }

    addPending(variantId, addQty);
    scheduleRefresh();
  };

  const handleClick = (e) => {
    const btn = e.target && e.target.closest ? e.target.closest("[data-btn-addtocart],[data-btn-addToCart]") : null;
    if (!btn) return;
    const form = findFormFromButton(btn);
    if (!form) return;
    handleAddAttempt(btn, form, e);
  };

  const handleSubmit = (e) => {
    const form = e.target;
    if (!form || !(form instanceof HTMLFormElement)) return;
    const btn = form.querySelector("[data-btn-addtocart],[data-btn-addToCart]");
    handleAddAttempt(btn, form, e);
  };

  const handleOutsideWarningClick = (e) => {
    if (!document.body.classList.contains("has-warning-ob")) return;
    const target = e.target;
    if (target && target.closest && target.closest("#online-buffer-warning-ob")) return;
    hideWarning();
  };

  const safeJsonParse = (text) => {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  };

  const computeVariantMax = (rec, variantId) => {
    if (!rec || rec.buffer <= 0) return null;
    const v = rec.variants ? rec.variants[String(variantId)] : null;
    if (!v || !v.inventory_management) return null;

    const invQty = toInt(v.inventory_quantity);
    let max = invQty - rec.buffer;
    if (max < 0) max = 0;

    const ruleMax = v.quantity_rule_max;
    if (ruleMax != null) {
      const r = toInt(ruleMax);
      if (r > 0 && max > r) max = r;
    }

    return max;
  };

  const disableWithRestore = (el) => {
    if (!el || !el.dataset) return;
    if (el.dataset.obPrevDisabled == null) el.dataset.obPrevDisabled = el.disabled ? "1" : "0";
    el.disabled = true;
    if (el.tagName === "BUTTON") el.setAttribute("aria-disabled", "true");
  };

  const restoreDisabled = (el) => {
    if (!el || !el.dataset) return;
    const prev = el.dataset.obPrevDisabled;
    if (prev == null) return;
    delete el.dataset.obPrevDisabled;
    if (prev === "0") {
      el.disabled = false;
      if (el.tagName === "BUTTON") el.removeAttribute("aria-disabled");
    }
  };

  const clampQuantityInput = (form, input) => {
    if (!form || !form.dataset || !input) return;
    const raw = form.dataset.onlineBufferEffective;
    if (raw == null || raw === "") return;
    const max = toInt(raw);
    if (max <= 0) return;

    let v = toInt(input.value);
    if (v < 1) v = 1;
    if (v > max) v = max;

    if (String(input.value) !== String(v)) input.value = String(v);
  };

  const stashDisplay = (el, valKey, prioKey, hadKey) => {
    if (!el || !el.dataset) return;
    if (el.dataset[hadKey] != null) return;
    const val = el.style.getPropertyValue("display");
    const prio = el.style.getPropertyPriority("display");
    const had = val !== "" || prio !== "";
    el.dataset[hadKey] = had ? "1" : "0";
    el.dataset[valKey] = val || "";
    el.dataset[prioKey] = prio || "";
  };

  const restoreDisplay = (el, valKey, prioKey, hadKey) => {
    if (!el || !el.dataset) return;
    const had = el.dataset[hadKey];
    if (had == null) return;
    const val = el.dataset[valKey] || "";
    const prio = el.dataset[prioKey] || "";
    delete el.dataset[hadKey];
    delete el.dataset[valKey];
    delete el.dataset[prioKey];
    if (had === "1") el.style.setProperty("display", val, prio);
    else el.style.removeProperty("display");
  };

  const stashText = (el, textKey, hadKey) => {
    if (!el || !el.dataset) return;
    if (el.dataset[hadKey] != null) return;
    el.dataset[hadKey] = "1";
    el.dataset[textKey] = el.textContent || "";
  };

  const restoreText = (el, textKey, hadKey) => {
    if (!el || !el.dataset) return;
    const had = el.dataset[hadKey];
    if (had == null) return;
    const val = el.dataset[textKey] || "";
    delete el.dataset[hadKey];
    delete el.dataset[textKey];
    el.textContent = val;
  };

  const updateMstAppBlocks = (applies, max) => {
    const blocks = document.querySelectorAll(MST_APP_BLOCK_SELECTOR);
    if (!blocks || blocks.length === 0) return;

    if (!applies || max == null) {
      for (const block of blocks) {
        if (!block || !block.dataset) continue;
        if (block.dataset.obMstControlled !== "1") continue;
        const wrap = block.querySelector(".iStock-wrapper");
        const title = block.querySelector(".iStock-title");
        if (wrap) restoreDisplay(wrap, "obMstPrevWrapDisplay", "obMstPrevWrapPrio", "obMstPrevWrapHad");
        if (title) {
          restoreDisplay(title, "obMstPrevTitleDisplay", "obMstPrevTitlePrio", "obMstPrevTitleHad");
          restoreText(title, "obMstPrevTitleText", "obMstPrevTitleTextHad");
        }
        restoreDisplay(block, "obMstPrevDisplay", "obMstPrevPrio", "obMstPrevHad");
        delete block.dataset.obMstControlled;
      }
      return;
    }

    const show = max <= 0;

    for (const block of blocks) {
      if (!block || !block.dataset) continue;

      const wrap = block.querySelector(".iStock-wrapper");
      const title = block.querySelector(".iStock-title");

      if (block.dataset.obMstControlled !== "1") {
        stashDisplay(block, "obMstPrevDisplay", "obMstPrevPrio", "obMstPrevHad");
        block.dataset.obMstControlled = "1";
      }

      if (wrap) stashDisplay(wrap, "obMstPrevWrapDisplay", "obMstPrevWrapPrio", "obMstPrevWrapHad");
      if (title) {
        stashDisplay(title, "obMstPrevTitleDisplay", "obMstPrevTitlePrio", "obMstPrevTitleHad");
        stashText(title, "obMstPrevTitleText", "obMstPrevTitleTextHad");
      }

      if (show) {
        block.style.setProperty("display", "block", "important");
        if (wrap) wrap.style.setProperty("display", "block", "important");
        if (title) {
          title.style.setProperty("display", "block", "important");
          if (String(title.textContent || "").trim() === "") title.textContent = MST_BUFFER_TITLE_TEXT;
        }
      } else {
        if (wrap) wrap.style.setProperty("display", "none", "important");
        if (title) title.style.setProperty("display", "none", "important");
        block.style.setProperty("display", "none", "important");
      }
    }
  };

  const updateProductForm = (form) => {
    const rec = state.productForms.get(form);
    if (!rec) return;

    const variantId = getVariantId(form);
    if (!variantId) return;

    const max = computeVariantMax(rec, variantId);

    rec.lastVariantId = variantId;
    rec.lastMax = max;

    const buffer = rec.buffer;
    const applies = buffer > 0 && max != null;

    if (buffer > 0) {
      form.dataset.onlineBuffer = String(buffer);
      form.dataset.onlineBufferApplies = "1";
    } else {
      delete form.dataset.onlineBuffer;
      form.dataset.onlineBufferApplies = "0";
    }

    if (max != null) form.dataset.onlineBufferEffective = String(max);
    else delete form.dataset.onlineBufferEffective;

    const btns = form.querySelectorAll("[data-btn-addtocart],[data-btn-addToCart]");
    for (const btn of btns) {
      if (!btn.dataset) continue;

      if (buffer > 0) {
        btn.dataset.onlineBuffer = String(buffer);
        btn.dataset.onlineBufferApplies = "1";
        btn.dataset.onlineBufferVariantId = String(variantId);
      } else {
        delete btn.dataset.onlineBuffer;
        btn.dataset.onlineBufferApplies = "0";
        delete btn.dataset.onlineBufferVariantId;
      }

      if (max != null) btn.dataset.onlineBufferEffective = String(max);
      else delete btn.dataset.onlineBufferEffective;

      if (applies && max === 0) disableWithRestore(btn);
      else restoreDisabled(btn);
    }

    const qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput) {
      if (applies) {
        qtyInput.setAttribute("max", String(max));
        clampQuantityInput(form, qtyInput);
        if (max === 0) disableWithRestore(qtyInput);
        else restoreDisabled(qtyInput);
      } else {
        qtyInput.removeAttribute("max");
        restoreDisabled(qtyInput);
      }
    }

    updateMstAppBlocks(applies, max);
  };

  const bindProductForm = (form) => {
    const rec = state.productForms.get(form);
    if (!rec || rec.bound) return;
    rec.bound = true;

    form.addEventListener(
      "change",
      () => {
        requestAnimationFrame(() => updateProductForm(form));
        setTimeout(() => updateProductForm(form), 0);
      },
      true
    );

    form.addEventListener(
      "input",
      (e) => {
        const t = e.target;
        if (!t || t.tagName !== "INPUT") return;
        if (t.getAttribute("name") !== "quantity") return;
        clampQuantityInput(form, t);
      },
      true
    );

    form.addEventListener(
      "blur",
      (e) => {
        const t = e.target;
        if (!t || t.tagName !== "INPUT") return;
        if (t.getAttribute("name") !== "quantity") return;
        clampQuantityInput(form, t);
      },
      true
    );

    const interval = setInterval(() => {
      updateProductForm(form);
    }, 500);

    state.productFormIntervals.set(form, interval);
  };

  const initProductForms = () => {
    const scripts = document.querySelectorAll("script[data-online-buffer-product-variants]");
    for (const script of scripts) {
      const form = script.closest("form");
      if (!form) continue;
      if (state.productForms.has(form)) continue;

      const cfg = safeJsonParse(script.textContent || "");
      if (!cfg || typeof cfg !== "object") continue;

      const buffer = toInt(cfg.buffer);
      const variants = cfg.variants && typeof cfg.variants === "object" ? cfg.variants : {};

      state.productForms.set(form, { buffer, variants, lastVariantId: null, lastMax: null, bound: false });
      bindProductForm(form);
      updateProductForm(form);
    }
  };

  const setRowQtyInputs = (row, desired) => {
    const inputs = row.querySelectorAll(CART_QTY_INPUT_SELECTOR);
    for (const inp of inputs) {
      if (!inp) continue;
      if (String(inp.value) !== String(desired)) inp.value = String(desired);
    }
  };

  const getCartLineContextFromTarget = (el) => {
    const row =
      (el && el.closest ? el.closest(CART_DRAWER_ITEM_SELECTOR) : null) ||
      (el && el.closest ? el.closest(CART_PAGE_ITEM_SELECTOR) : null);

    if (!row || !row.dataset) return null;
    if (!isTruthy(row.dataset.onlineBufferApplies)) return null;

    let maxRaw = row.dataset.onlineBufferMax;
    if (maxRaw == null || maxRaw === "") maxRaw = row.dataset.onlineBufferEffective;
    if (maxRaw == null || maxRaw === "") return null;

    const max = toInt(maxRaw);
    if (max < 0) return null;

    const box = el && el.closest ? el.closest("cart-update-quantity") : null;
    let input = null;
    if (box) input = box.querySelector(CART_QTY_INPUT_SELECTOR);
    if (!input) input = row.querySelector(CART_QTY_INPUT_SELECTOR);
    if (!input) return null;

    const key = row.dataset.onlineBufferKey || row.dataset.line || (input.dataset ? input.dataset.line : null);
    if (!key) return null;

    return { row, max, input, key };
  };

  const changeLineItemQuantity = async (key, qty) => {
    const body = new URLSearchParams();
    body.set("id", String(key));
    body.set("quantity", String(qty));

    const r = await fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
      body: body.toString()
    });

    if (!r.ok) return null;

    const c = await r.json().catch(() => null);
    if (c) applyCartObject(c);
    return c;
  };

  const getCartLineRows = () => {
    const a = Array.from(document.querySelectorAll(CART_DRAWER_ITEM_SELECTOR));
    const b = Array.from(document.querySelectorAll(CART_PAGE_ITEM_SELECTOR));
    const out = [];
    const seen = new Set();
    for (const el of a.concat(b)) {
      if (!el || seen.has(el)) continue;
      seen.add(el);
      out.push(el);
    }
    return out;
  };

  const scheduleCartDrawerEnforce = (delay = 150) => {
    clearTimeout(state.cartDrawerTimer);
    state.cartDrawerTimer = setTimeout(() => {
      enforceCartDrawerLines();
    }, delay);
  };

  const enforceCartDrawerLines = async () => {
    if (state.enforcingCartDrawer) {
      state.enforceCartDrawerQueued = true;
      return;
    }

    state.enforcingCartDrawer = true;
    state.enforceCartDrawerQueued = false;

    try {
      const rows = getCartLineRows();
      if (!rows || rows.length === 0) return;

      for (const row of rows) {
        if (!row || !row.dataset) continue;
        if (!isTruthy(row.dataset.onlineBufferApplies)) continue;

        let maxRaw = row.dataset.onlineBufferMax;
        if (maxRaw == null || maxRaw === "") maxRaw = row.dataset.onlineBufferEffective;
        if (maxRaw == null || maxRaw === "") continue;

        const max = toInt(maxRaw);
        if (max < 0) continue;

        const inputs = row.querySelectorAll(CART_QTY_INPUT_SELECTOR);
        if (!inputs || inputs.length === 0) continue;

        const qty = toInt(inputs[0].value);
        if (qty <= max) continue;

        const key =
          row.dataset.onlineBufferKey ||
          row.dataset.line ||
          (inputs[0].dataset ? inputs[0].dataset.line : null);

        if (!key) continue;

        const desired = max;

        setRowQtyInputs(row, desired);

        if (desired <= 0) showWarning("This item is sold out online and was removed from your cart.");
        else showWarning(`You can only add ${desired} of this item online.`);

        await changeLineItemQuantity(key, desired);
        scheduleRefresh();
        break;
      }
    } finally {
      state.enforcingCartDrawer = false;
      if (state.enforceCartDrawerQueued) scheduleCartDrawerEnforce(200);
    }
  };

  const handleCartQtyClick = (e) => {
    const plus = e.target && e.target.closest ? e.target.closest(CART_PLUS_SELECTOR) : null;
    const minus = e.target && e.target.closest ? e.target.closest(CART_MINUS_SELECTOR) : null;
    if (!plus && !minus) return;

    const ctx = getCartLineContextFromTarget(plus || minus);
    if (!ctx || !ctx.input) return;

    const current = toInt(ctx.input.value);

    if (plus) {
      if (ctx.max <= 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showWarning("This item is sold out online.");
        scheduleCartDrawerEnforce(50);
        return;
      }

      if (current >= ctx.max) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showWarning(`You can only add ${ctx.max} of this item online.`);
        return;
      }

      scheduleCartDrawerEnforce(650);
      return;
    }

    if (minus) {
      scheduleCartDrawerEnforce(650);
    }
  };

  const handleCartQtyField = (e) => {
    const t = e.target;
    if (!t || t.tagName !== "INPUT") return;
    if (!t.hasAttribute("data-cart-quantity")) return;

    const ctx = getCartLineContextFromTarget(t);
    if (!ctx || !ctx.input) return;

    const v = toInt(ctx.input.value);

    if (ctx.max <= 0) {
      if (v > 0) {
        setRowQtyInputs(ctx.row, 0);
        showWarning("This item is sold out online and was removed from your cart.");
        scheduleCartDrawerEnforce(50);
      }
      return;
    }

    if (v > ctx.max) {
      setRowQtyInputs(ctx.row, ctx.max);
      showWarning(`You can only add ${ctx.max} of this item online.`);
      scheduleCartDrawerEnforce(50);
    }
  };

  const initCartDrawerObserver = () => {
    if (state.cartDrawerObserver) return;

    const mo = new MutationObserver(() => {
      if (document.querySelector(CART_DRAWER_ITEM_SELECTOR) || document.querySelector(CART_PAGE_ITEM_SELECTOR)) {
        scheduleCartDrawerEnforce(200);
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });
    state.cartDrawerObserver = mo;
  };

  const init = () => {
    document.documentElement.setAttribute("data-online-buffer-engine", "1");
    fetchCart(true);
    initProductForms();
    document.addEventListener("click", handleClick, true);
    document.addEventListener("submit", handleSubmit, true);
    document.addEventListener("click", handleOutsideWarningClick, true);
    document.addEventListener("click", handleCartQtyClick, true);
    document.addEventListener("change", handleCartQtyField, true);
    document.addEventListener("blur", handleCartQtyField, true);
    initCartDrawerObserver();
    window.addEventListener("focus", () => {
      fetchCart(true);
      scheduleCartDrawerEnforce(300);
    });
    scheduleCartDrawerEnforce(500);
    window.dispatchEvent(new CustomEvent("onlinebuffer:ready"));
  };

  Engine.fetchCart = () => fetchCart(true);
  Engine.scanProductForms = () => initProductForms();
  window.OnlineBufferEngine = Engine;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
